import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneCall, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Click-to-dial via Aircall from the Pre-Funding Call dialog. Pass
// `presetNumber` + `contactLabel` to dial someone other than the customer
// (e.g. a PG) — the dialog prefills that number instead.
export const AircallDialButton = ({ application, presetNumber, contactLabel, testId }) => {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [userId, setUserId] = useState("");
  const [numberId, setNumberId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [busy, setBusy] = useState(false);

  const isAus = application?.currency === "AUD" || ["AUS", "AU"].includes((application?.country || "").toUpperCase());
  // Aircall requires E.164 — locally-typed numbers ("07496 095912") fail the dial.
  const normalizePhone = (raw) => {
    const t = (raw || "").trim();
    const digits = t.replace(/\D/g, "");
    if (!digits) return "";
    if (t.startsWith("+")) return "+" + digits;
    if (digits.startsWith("00")) return "+" + digits.slice(2);
    if (digits.startsWith("0")) return (isAus ? "+61" : "+44") + digits.slice(1);
    if (digits.startsWith(isAus ? "61" : "44")) return "+" + digits;
    return "+" + (isAus ? "61" : "44") + digits;
  };

  const me = (() => {
    try { return JSON.parse(localStorage.getItem("sf_user") || "{}")?.email || "portal"; }
    catch { return "portal"; }
  })();

  useEffect(() => {
    if (!open) return;
    setToNumber(
      presetNumber ||
      application?.loandisk_loan_data?.borrower_mobile ||
      application?.customer_phone || application?.mobile || ""
    );
    api.get("/sales/calendar/aircall-meta")
      .then((r) => {
        setMeta(r.data);
        const users = r.data.users || [];
        const mine = users.find((u) => (u.email || "").toLowerCase() === me.toLowerCase());
        if (mine) setUserId(String(mine.id));
        else if (users.length === 1) setUserId(String(users[0].id));
        const nums = r.data.numbers || [];
        // Default to a caller ID matching the deal's region (UK deals were
        // defaulting to the first number on the account — an AUS line).
        const pref = nums.find((n) => String(n.digits || "").replace(/[\s-]/g, "").startsWith(isAus ? "+61" : "+44"));
        const pick = pref || nums[0];
        if (pick) setNumberId(String(pick.id));
      })
      .catch((e) => toast.error("Aircall unavailable: " + errorDetailMessage(e, e.message)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const dial = async () => {
    try {
      setBusy(true);
      await api.post(`/sales/calendar/click-to-dial?actor=${encodeURIComponent(me)}`, {
        to_number: normalizePhone(toNumber),
        user_id: parseInt(userId, 10),
        from_number_id: parseInt(numberId, 10),
        deal_name: application?.company_name || application?.application_id || "",
      });
      toast.success("Dial pushed to your Aircall app — accept it to place the call");
      setOpen(false);
    } catch (e) {
      toast.error("Dial failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button type="button" size="sm" variant="outline"
        className="h-7 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50 ml-2"
        onClick={() => setOpen(true)} data-testid={testId || "prefunding-dial-btn"}>
        <PhoneCall className="w-3 h-3 mr-1" />{contactLabel || "Call customer"}
      </Button>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-md" data-testid="prefunding-dial-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-blue-600" />
                Call via Aircall — {application?.company_name || application?.application_id}
              </DialogTitle>
              <DialogDescription>
                Pushes a click-to-dial to your Aircall desktop/mobile app (you must be online in Aircall).
              </DialogDescription>
            </DialogHeader>
            {!meta ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading Aircall…
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Customer number *</Label>
                  <Input value={toNumber} onChange={(e) => setToNumber(e.target.value)}
                    placeholder="+447…" data-testid="prefunding-dial-number" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Your Aircall seat *</Label>
                    <Select value={userId || undefined} onValueChange={setUserId}>
                      <SelectTrigger className="h-9" data-testid="prefunding-dial-user">
                        <SelectValue placeholder="Pick user…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {(meta.users || []).map((u) => (
                          <SelectItem key={u.id} value={String(u.id)}>{u.name || u.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Call from number *</Label>
                    <Select value={numberId || undefined} onValueChange={setNumberId}>
                      <SelectTrigger className="h-9" data-testid="prefunding-dial-from">
                        <SelectValue placeholder="Pick line…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {(meta.numbers || []).map((n) => (
                          <SelectItem key={n.id} value={String(n.id)}>{n.name || n.digits}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button className="w-full" onClick={dial}
                  disabled={busy || !toNumber.trim() || !userId || !numberId}
                  data-testid="prefunding-dial-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                  Push call to Aircall
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AircallDialButton;
